import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { type ConfigRestore, isRestoreInProgress } from './useConfigSnapshots';
import { config } from '../config';

// Define interfaces for Change Management and related types
export interface ChangeManagement {
  id: string;
  contextId: string;
  divisionId: string | null;
  organizationId: string;
  title: string;
  description: string | null;
  state: CMState;
  suppressAlerts: boolean;
  startTime: string | null;
  expectedEndTime: string | null;
  actualEndTime: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  approvalStatus?: ApprovalStatus;
  isAutomated: boolean;
  automationSource: string | null;
  automationTriggeredBy: string | null;
  planProgress?: PlanProgress | null;
  autoStart: boolean;
  autoStartMissedAt: string | null;
  needsMyApproval: boolean;
  changeType: ChangeType;
  riskLevel: RiskLevel | null;
}

export type ChangeType = 'standard' | 'normal' | 'emergency';
export type RiskLevel = 'low' | 'medium' | 'high';

export interface CMSettings {
  autoStartEnabled: boolean;
}

export interface ApprovalStatus {
  totalApprovers: number;
  approvedCount: number;
  allApproved: boolean;
}

export interface PlanProgress {
  planCount: number;
  totalSteps: number;
  completedSteps: number;
  hasFailedPlan: boolean;
}

export interface AffectedDevice {
  deviceId: string;
  name: string | null;
  deviceType: string;
  ipAddr: string | null;
}

export type CMAlertRelationship = 'suppressed' | 'unexpected' | 'expected' | 'remediates';

export interface CMAlert {
  relationship: CMAlertRelationship;
  deviceId: string | null;
  deviceName: string | null;
  dedupeKey: string | null;
  title: string | null;
  incidentId: string | null;
  incidentStatus: string | null;
  firstSeen: string;
  lastSeen: string;
}

export interface CMSnapshotPhase {
  capturedAt: string;
  configSnapshotId: string | null;
  configText: string | null;
  checksum: string | null;
  format: string | null;
  note: string | null;
}

export interface CMSnapshotDevice {
  deviceId: string;
  deviceName: string;
  pre: CMSnapshotPhase | null;
  post: CMSnapshotPhase | null;
  changed: boolean;
}

export interface CMEvent {
  id: string;
  cmId: string;
  eventType: string;
  content: string | null;
  metadata: Record<string, unknown> | null;
  createdBy: string;
  /** Resolved actor display name; null means the system (automated). */
  createdByName: string | null;
  createdAt: string;
}

export type CMState =
  | 'draft'
  | 'waiting_approval'
  | 'approved'
  | 'rejected'
  | 'in_progress'
  | 'paused'
  | 'verification'
  | 'completed'
  | 'cancelled'
  | 'partially_completed';

interface CMListResponse {
  cms: ChangeManagement[];
  total: number;
}

interface CMEventsResponse {
  events: CMEvent[];
}

export interface CreateCMRequest {
  contextId: string;
  title: string;
  description?: string;
  suppressAlerts?: boolean;
  startTime?: string; // RFC3339 format
  expectedEndTime?: string; // RFC3339 format
  autoStart?: boolean;
  isAutomated?: boolean;
  automationSource?: string;
  automationTriggeredBy?: string;
  changeType?: ChangeType;
  riskLevel?: RiskLevel;
}

export interface UpdateCMRequest {
  title?: string;
  description?: string;
  suppressAlerts?: boolean;
  startTime?: string; // RFC3339 format
  expectedEndTime?: string; // RFC3339 format
  autoStart?: boolean;
  changeType?: ChangeType;
  riskLevel?: RiskLevel;
}

export interface AddRoleRequest {
  roleType: 'user' | 'team';
  roleId: string;
}

export interface AddCommentRequest {
  content: string;
}

export interface RoleResponse {
  roleType: 'user' | 'team';
  roleId: string;
  addedBy: string;
  addedAt: string;
  approvedByUserId?: string | null;
  approvalTime?: string | null;
}

export interface RoleListResponse {
  roles: RoleResponse[];
}

export interface CMPlan {
  planId: string;
  position: number;
  title: string;
  description: string | null;
  state: string;
  stepCount: number;
  completedStepCount: number;
  addedBy: string;
  addedAt: string;
}

interface CMPlansResponse {
  plans: CMPlan[];
}

export interface AttachPlanRequest {
  planId: string;
}

export interface ReorderPlanRequest {
  newPosition: number;
}

export interface ListCMsFilters {
  state?: string;
  contextId?: string;
  includeArchived?: boolean;
  isAutomated?: boolean;
  automationSource?: string;
}

const backendApi = config.backendApi;

// Fetch all change managements with optional filters
const fetchCMs = async (filters?: ListCMsFilters): Promise<ChangeManagement[]> => {
  const params = new URLSearchParams();
  if (filters?.state) params.append('state', filters.state);
  if (filters?.contextId) params.append('context_id', filters.contextId);
  if (filters?.includeArchived) params.append('include_archived', 'true');
  if (filters?.isAutomated !== undefined) params.append('is_automated', String(filters.isAutomated));
  if (filters?.automationSource) params.append('automation_source', filters.automationSource);

  const queryString = params.toString();
  const url = `${backendApi}/api/cms${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get<CMListResponse>(url, { withCredentials: true });
  return response.data.cms;
};

// Fetch a single change management by ID
const fetchCM = async (cmId: string): Promise<ChangeManagement> => {
  const response = await axios.get<ChangeManagement>(`${backendApi}/api/cms/${cmId}`, {
    withCredentials: true
  });
  return response.data;
};

// Fetch events for a change management
const fetchCMEvents = async (cmId: string): Promise<CMEvent[]> => {
  const response = await axios.get<CMEventsResponse>(
    `${backendApi}/api/cms/${cmId}/events`,
    { withCredentials: true }
  );
  return response.data.events;
};

// Create a new change management
const createCM = async (data: CreateCMRequest): Promise<ChangeManagement> => {
  const response = await axios.post<ChangeManagement>(`${backendApi}/api/cms`, data, {
    withCredentials: true
  });
  return response.data;
};

export interface CreateCMFromIncidentRequest {
  /** One incident. Send this or `incidentIds` (or both — they union). */
  incidentId?: string;
  /**
   * A set of incidents. Audience partitioning means one finding is several
   * incidents, one per division that owns some of its devices, so remediating the
   * whole thing means naming them all — fetch the siblings from
   * `GET /api/incidents/{id}/related`, which returns only the ones you may read.
   * Each id is gated on `incident:read`, so you can only build from incidents you
   * can already open.
   */
  incidentIds?: string[];
  contextId: string;
  title?: string;
  description?: string;
  /**
   * Which remediation option (by id) to materialize into a plan. Applied per
   * incident: the partitions of one finding offer options under the same ids, each
   * already filtered to its own devices, so one choice covers the set.
   */
  remediationId?: string;
}

const createCMFromIncident = async (
  data: CreateCMFromIncidentRequest
): Promise<ChangeManagement> => {
  const response = await axios.post<ChangeManagement>(
    `${backendApi}/api/cms/from-incident`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Update a change management
const updateCM = async (cmId: string, data: UpdateCMRequest): Promise<ChangeManagement> => {
  const response = await axios.put<ChangeManagement>(
    `${backendApi}/api/cms/${cmId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Archive a change management
const archiveCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/archive`,
    {},
    { withCredentials: true }
  );
};

// Workflow state transitions
const submitCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/submit`,
    {},
    { withCredentials: true }
  );
};

const approveCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/approve`,
    {},
    { withCredentials: true }
  );
};

const rejectCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/reject`,
    {},
    { withCredentials: true }
  );
};

const startCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/start`,
    {},
    { withCredentials: true }
  );
};

const pauseCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/pause`,
    {},
    { withCredentials: true }
  );
};

const resumeCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/resume`,
    {},
    { withCredentials: true }
  );
};

const completeCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/complete`,
    {},
    { withCredentials: true }
  );
};

const cancelCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/cancel`,
    {},
    { withCredentials: true }
  );
};

/** A device a rollback did not restore, and why. */
export interface RollbackSkippedDevice {
  deviceId: string;
  deviceName: string | null;
  reason: string;
}

/** What rolling back a change asked for: a restore of each device it could. */
export interface RollbackResult {
  restores: ConfigRestore[];
  skipped: RollbackSkippedDevice[];
}

const rollbackCM = async (cmId: string): Promise<RollbackResult> => {
  const res = await axios.post<RollbackResult>(
    `${backendApi}/api/cms/${cmId}/rollback`,
    {},
    { withCredentials: true }
  );
  return res.data;
};

/** A restore a change's rollback asked for, with the device it restored. */
export interface CMRestore extends ConfigRestore {
  deviceName: string | null;
}

const fetchCMRestores = async (cmId: string): Promise<CMRestore[]> => {
  const res = await axios.get<{ restores: CMRestore[] }>(
    `${backendApi}/api/cms/${cmId}/restores`,
    { withCredentials: true }
  );
  return res.data.restores;
};

export interface RollbackDevicePreview {
  device_id: string;
  device_name: string | null;
  diff: string | null;
  error: string | null;
  /** What to know before rolling the device back, one per line. */
  warnings?: string | null;
}

const previewRollback = async (cmId: string): Promise<RollbackDevicePreview[]> => {
  const res = await axios.post<{ devices: RollbackDevicePreview[] }>(
    `${backendApi}/api/cms/${cmId}/rollback/preview`,
    {},
    { withCredentials: true }
  );
  return res.data.devices;
};

const retryCM = async (cmId: string): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/retry`,
    {},
    { withCredentials: true }
  );
};

// Participant management
const addOwner = async (cmId: string, data: AddRoleRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/owners`,
    data,
    { withCredentials: true }
  );
};

const addApprover = async (cmId: string, data: AddRoleRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/approvers`,
    data,
    { withCredentials: true }
  );
};

const addWatcher = async (cmId: string, data: AddRoleRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/watchers`,
    data,
    { withCredentials: true }
  );
};

const addAssignee = async (cmId: string, data: AddRoleRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/assignees`,
    data,
    { withCredentials: true }
  );
};

// Add a comment to a change management
const addComment = async (cmId: string, data: AddCommentRequest): Promise<CMEvent> => {
  const response = await axios.post<CMEvent>(
    `${backendApi}/api/cms/${cmId}/comments`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Plan management
const fetchCMPlans = async (cmId: string): Promise<CMPlan[]> => {
  const response = await axios.get<CMPlansResponse>(
    `${backendApi}/api/cms/${cmId}/plans`,
    { withCredentials: true }
  );
  return response.data.plans;
};

const attachPlan = async (cmId: string, data: AttachPlanRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/plans`,
    data,
    { withCredentials: true }
  );
};

const detachPlan = async (cmId: string, planId: string): Promise<void> => {
  await axios.delete(
    `${backendApi}/api/cms/${cmId}/plans/${planId}`,
    { withCredentials: true }
  );
};

const reorderCMPlan = async (cmId: string, planId: string, data: ReorderPlanRequest): Promise<void> => {
  await axios.post(
    `${backendApi}/api/cms/${cmId}/plans/${planId}/reorder`,
    data,
    { withCredentials: true }
  );
};

// Fetch participant lists
const fetchOwners = async (cmId: string): Promise<RoleResponse[]> => {
  const response = await axios.get<RoleListResponse>(
    `${backendApi}/api/cms/${cmId}/owners`,
    { withCredentials: true }
  );
  return response.data.roles;
};

const fetchApprovers = async (cmId: string): Promise<RoleResponse[]> => {
  const response = await axios.get<RoleListResponse>(
    `${backendApi}/api/cms/${cmId}/approvers`,
    { withCredentials: true }
  );
  return response.data.roles;
};

const fetchWatchers = async (cmId: string): Promise<RoleResponse[]> => {
  const response = await axios.get<RoleListResponse>(
    `${backendApi}/api/cms/${cmId}/watchers`,
    { withCredentials: true }
  );
  return response.data.roles;
};

const fetchAssignees = async (cmId: string): Promise<RoleResponse[]> => {
  const response = await axios.get<RoleListResponse>(
    `${backendApi}/api/cms/${cmId}/assignees`,
    { withCredentials: true }
  );
  return response.data.roles;
};

const fetchAffectedDevices = async (cmId: string): Promise<AffectedDevice[]> => {
  const response = await axios.get<{ devices: AffectedDevice[] }>(
    `${backendApi}/api/cms/${cmId}/affected-devices`,
    { withCredentials: true }
  );
  return response.data.devices;
};

const fetchCMAlerts = async (cmId: string): Promise<CMAlert[]> => {
  const response = await axios.get<{ alerts: CMAlert[] }>(
    `${backendApi}/api/cms/${cmId}/alerts`,
    { withCredentials: true }
  );
  return response.data.alerts;
};

const fetchCMSnapshots = async (cmId: string): Promise<CMSnapshotDevice[]> => {
  const response = await axios.get<{ devices: CMSnapshotDevice[] }>(
    `${backendApi}/api/cms/${cmId}/snapshots`,
    { withCredentials: true }
  );
  return response.data.devices;
};

const fetchCMSettings = async (): Promise<CMSettings> => {
  const response = await axios.get<CMSettings>(`${backendApi}/api/cms/settings`, {
    withCredentials: true,
  });
  return response.data;
};

const updateCMSettings = async (data: CMSettings): Promise<CMSettings> => {
  const response = await axios.put<CMSettings>(`${backendApi}/api/cms/settings`, data, {
    withCredentials: true,
  });
  return response.data;
};

// Hook to fetch all change managements
export function useChangeManagements(
  organizationId: string | null | undefined,
  filters?: ListCMsFilters
) {
  const contextReady = useActiveContextReady();

  return useQuery<ChangeManagement[]>({
    queryKey: ['change-managements', organizationId, filters],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch change managements.");
      }
      return fetchCMs(filters);
    },
    enabled: !!organizationId && contextReady,
  });
}

// Hook to fetch a single change management
export function useChangeManagement(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<ChangeManagement>({
    queryKey: ['change-management', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch change management.");
      }
      return fetchCM(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export interface SchedulingConflict {
  cmId: string;
  title: string;
  startTime: string | null;
  expectedEndTime: string | null;
  sharedDeviceCount: number;
}

interface SchedulingConflictsResponse {
  conflicts: SchedulingConflict[];
}

const fetchCMConflicts = async (cmId: string): Promise<SchedulingConflict[]> => {
  const response = await axios.get<SchedulingConflictsResponse>(
    `${backendApi}/api/cms/${cmId}/conflicts`,
    { withCredentials: true }
  );
  return response.data.conflicts;
};

// Other scheduled/active changes overlapping this one in time and sharing devices.
export function useCMConflicts(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<SchedulingConflict[]>({
    queryKey: ['cm-conflicts', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error('cmId is required to fetch CM conflicts.');
      }
      return fetchCMConflicts(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

// Hook to fetch change management events
export function useCMEvents(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<CMEvent[]>({
    queryKey: ['cm-events', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM events.");
      }
      return fetchCMEvents(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

// Hook to create a change management
export function useCreateCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCMRequest) => createCM(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    },
  });
}

export function useCreateCMFromIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCMFromIncidentRequest) => createCMFromIncident(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    },
  });
}

// Hook to update a change management
export function useUpdateCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: UpdateCMRequest }) =>
      updateCM(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    },
  });
}

// Hook to archive a change management
export function useArchiveCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => archiveCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    },
  });
}

// Workflow state transition hooks
export function useSubmitCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => submitCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
      // Submission locks attached plans and snapshots affected devices.
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useApproveCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => approveCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useRejectCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => rejectCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useStartCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => startCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function usePauseCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => pauseCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useResumeCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => resumeCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useCompleteCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => completeCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useCancelCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => cancelCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
    },
  });
}

export function useRollbackCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => rollbackCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-restores', cmId] });
    },
  });
}

/** Dry-run a rollback and return the per-device diffs it would apply. Does not
 *  change anything on the devices, so there is nothing to invalidate. */
export function usePreviewRollback() {
  return useMutation({
    mutationFn: (cmId: string) => previewRollback(cmId),
  });
}

// Participant management hooks
export function useAddOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AddRoleRequest }) =>
      addOwner(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-owners', variables.cmId] });
    },
  });
}

export function useAddApprover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AddRoleRequest }) =>
      addApprover(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-approvers', variables.cmId] });
    },
  });
}

export function useAddWatcher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AddRoleRequest }) =>
      addWatcher(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-watchers', variables.cmId] });
    },
  });
}

export function useAddAssignee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AddRoleRequest }) =>
      addAssignee(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-assignees', variables.cmId] });
    },
  });
}

// Hook to add a comment
export function useAddCMComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AddCommentRequest }) =>
      addComment(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
    },
  });
}

// Hooks to fetch participant lists
export function useCMOwners(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<RoleResponse[]>({
    queryKey: ['cm-owners', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM owners.");
      }
      return fetchOwners(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useCMApprovers(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<RoleResponse[]>({
    queryKey: ['cm-approvers', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM approvers.");
      }
      return fetchApprovers(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useCMWatchers(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<RoleResponse[]>({
    queryKey: ['cm-watchers', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM watchers.");
      }
      return fetchWatchers(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useCMAssignees(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<RoleResponse[]>({
    queryKey: ['cm-assignees', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM assignees.");
      }
      return fetchAssignees(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useCMSettings() {
  const contextReady = useActiveContextReady();

  return useQuery<CMSettings>({
    queryKey: ['cm-settings'],
    queryFn: fetchCMSettings,
    enabled: contextReady,
  });
}

export function useUpdateCMSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CMSettings) => updateCMSettings(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cm-settings'] });
    },
  });
}

export function useCMAlerts(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<CMAlert[]>({
    queryKey: ['cm-alerts', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error('cmId is required to fetch CM alerts.');
      }
      return fetchCMAlerts(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useCMSnapshots(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<CMSnapshotDevice[]>({
    queryKey: ['cm-snapshots', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error('cmId is required to fetch CM snapshots.');
      }
      return fetchCMSnapshots(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

/**
 * The restores the change's rollbacks asked for, newest first. Re-read every
 * few seconds while one is in progress, besides the websocket events.
 */
export function useCMRestores(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<CMRestore[]>({
    queryKey: ['cm-restores', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error('cmId is required to fetch CM restores.');
      }
      return fetchCMRestores(cmId);
    },
    enabled: !!cmId && contextReady,
    refetchInterval: (query) => (query.state.data?.some(isRestoreInProgress) ? 5000 : false),
  });
}

export function useCMAffectedDevices(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<AffectedDevice[]>({
    queryKey: ['cm-affected-devices', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error('cmId is required to fetch affected devices.');
      }
      return fetchAffectedDevices(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

// Plan management hooks
export function useCMPlans(cmId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<CMPlan[]>({
    queryKey: ['cm-plans', cmId],
    queryFn: () => {
      if (!cmId) {
        throw new Error("cmId is required to fetch CM plans.");
      }
      return fetchCMPlans(cmId);
    },
    enabled: !!cmId && contextReady,
  });
}

export function useAttachPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, data }: { cmId: string; data: AttachPlanRequest }) =>
      attachPlan(cmId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useDetachPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, planId }: { cmId: string; planId: string }) =>
      detachPlan(cmId, planId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', variables.cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useReorderCMPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cmId, planId, data }: { cmId: string; planId: string; data: ReorderPlanRequest }) =>
      reorderCMPlan(cmId, planId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', variables.cmId] });
    },
  });
}

export function useRetryCM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cmId: string) => retryCM(cmId),
    onSuccess: (_, cmId) => {
      void queryClient.invalidateQueries({ queryKey: ['change-management', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plan'] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', cmId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}
